import React, {useCallback, useEffect, useRef} from 'react';
import {useAuthenticationPrompt} from '../AuthenticationProvider/AuthenticationProvider';
import {useIsAuthenticated} from '../hooks/useIsAuthenticated';
import {useForceUpdate} from '@aboutyou/utils';
import {useIsDirectEntryCallback} from '@aboutyou/router';

interface AuthenticatedViewProps<ViewProps extends object> {
  /**
   * Passed down to the `useIsAuthenticated` hook to determine if we should consider the CO session for auth
   */
  includeCheckoutSession: boolean;

  /**
   * If true, the modal will display the guest option
   */
  allowGuestLogin?: boolean;

  /**
   * View component to return if the authentication is successful
   */
  View: React.ComponentType<ViewProps>;

  /**
   * Any additional props to provide to the View component
   */
  viewProps?: React.ComponentProps<React.ComponentType<ViewProps>>;

  /**
   * Component to show if the user is not authenticated
   */
  Loading?: React.ComponentType;

  /**
   * Component to show if the user cannot be authenticated e.g. they close the modal
   */
  Fallback: React.ComponentType;

  /**
   * This can be set to true if the view, which is rendered, requires a valid checkout session
   * in the view itself. It would then prevent error views in the background.
   */
  preventViewWhileLoading?: boolean;
}

export const AuthenticatedView = <ViewProps extends object>({
  View: ViewComponent,
  Loading: LoadingComponent,
  Fallback: FallbackComponent,
  includeCheckoutSession,
  allowGuestLogin,
  preventViewWhileLoading,
  viewProps,
}: AuthenticatedViewProps<ViewProps>) => {
  const getIsDirectEntry = useIsDirectEntryCallback();
  const hasSeenPrompt = useRef(false);

  const forceUpdate = useForceUpdate();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const {isUserDataLoading, isAuthenticated} = useIsAuthenticated();

  const showViewInBackground = isAuthenticated && !preventViewWhileLoading;

  const renderFallbackComponent = useRef(false);
  const renderViewComponent = useRef(showViewInBackground);

  const StaleViewComponent = useCallback(
    ViewComponent as any,
    [],
  ) as typeof ViewComponent;

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (isUserDataLoading) return;

    const isDirectEntry = getIsDirectEntry();

    if (!hasSeenPrompt.current) {
      hasSeenPrompt.current = true;

      showUserAuthenticationPrompt({
        /**
         * It is necessary to check the validity of the CO session only if the user is entering this view directly
         * This is because we execute `showUserAuthenticationPrompt` from different parts of the applications already before we render the AuthenticatedView
         * The CO session validation is only necessary here if the user directly enters the `AuthenticatedView` in which case the user bypass `showUserAuthenticationPrompt` call
         * for eg: when directly entering `/checkout` url in the browser
         * in other cases its perfectly fine to just check `isAuthenticated`
         */
        includeCheckoutSession: isDirectEntry && includeCheckoutSession,
        allowGuestLogin,
        onCancel: () => {
          renderFallbackComponent.current = true;
          forceUpdate();
        },
        onSuccess: () => {
          renderViewComponent.current = true;
          forceUpdate();
        },
      });
    }
  }, [allowGuestLogin, includeCheckoutSession, isUserDataLoading, forceUpdate]);

  if (renderViewComponent.current) {
    return <StaleViewComponent {...(viewProps as ViewProps)} />;
  }

  if (renderFallbackComponent.current) {
    // force rerender the Fallback View to force rerunning of useEffects in the FallbackComponent
    return <FallbackComponent key={`${Math.random()}`} />;
  }

  return LoadingComponent ? <LoadingComponent /> : null;
};
